import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Lightning, Briefcase, User, ArrowRight } from '@phosphor-icons/react';

const COLOR_OPTIONS = [
  { id: 'amber',   hex: '#F49F0A', label: 'Amber'   },
  { id: 'emerald', hex: '#10B981', label: 'Emerald' },
  { id: 'sky',     hex: '#38BDF8', label: 'Sky'     },
  { id: 'purple',  hex: '#A855F7', label: 'Purple'  },
];

export default function PersonaSetupPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { personas, fetchPersonas, createPersona, setActivePersonaId, loading } = useApp();

  // personaId passed via router state from register_route response
  const personaId = location.state?.personaId;

  const [name, setName] = useState('');
  const [roleTitle, setRoleTitle] = useState('');
  const [color, setColor] = useState('purple');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Resolve the default persona to pre-fill its name & color
  useEffect(() => {
    fetchPersonas();
  }, [fetchPersonas]);

  useEffect(() => {
    if (!personaId && personas.length > 0) {
      const first = personas[0];
      setColor(first.color || 'purple');
    }
  }, [personas, personaId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setError('Please enter a persona name.'); return; }

    setBusy(true);
    setError('');
    try {
      // Find the persona to update (auto-created on register) or create fresh if missing
      let targetId = personaId;

      if (targetId) {
        // Update the auto-created default persona
        await fetch(
          `${process.env.REACT_APP_BACKEND_URL ? process.env.REACT_APP_BACKEND_URL + '/api' : '/api'}/personas/${targetId}`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${localStorage.getItem('sankhya_token')}`,
            },
            body: JSON.stringify({ name: name.trim(), color, role_title: roleTitle.trim(), description: '' }),
          }
        );
        setActivePersonaId(targetId);
      } else {
        // Fallback: create a new persona (edge case)
        const p = await createPersona({ name: name.trim(), color, role_title: roleTitle.trim(), description: '' });
        targetId = p?.id;
        if (targetId) setActivePersonaId(targetId);
      }

      await fetchPersonas();
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError('Could not save persona. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-amber-500 mb-4">
            <Lightning size={18} weight="fill" className="text-black" />
          </div>
          <h1 className="text-2xl font-bold text-white font-['Outfit'] tracking-tight">
            Set up your first persona
          </h1>
          <p className="text-sm text-zinc-500 mt-2">
            A persona is your job-search identity — give it a name and target role.
          </p>
        </div>

        <div className="bg-[#111111] border border-white/8 rounded-xl p-8 shadow-2xl">
          {error && (
            <div className="mb-5 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-xs text-red-300">
              {error}
            </div>
          )}

          <form onSubmit={submit} className="space-y-5">
            {/* Persona name */}
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">
                Persona Name *
              </label>
              <div className="relative">
                <User size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="e.g. Product Manager, Senior Dev…"
                  maxLength={100}
                  autoFocus
                  className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg pl-9 pr-4 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-amber-500/60 transition-colors"
                />
              </div>
            </div>

            {/* Target job title */}
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">
                Target Job Title <span className="text-zinc-600">(optional)</span>
              </label>
              <div className="relative">
                <Briefcase size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
                <input
                  type="text"
                  value={roleTitle}
                  onChange={e => setRoleTitle(e.target.value)}
                  placeholder="e.g. Senior Product Manager"
                  maxLength={200}
                  className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg pl-9 pr-4 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-amber-500/60 transition-colors"
                />
              </div>
            </div>

            {/* Color picker */}
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 block">
                Persona Color
              </label>
              <div className="flex gap-3">
                {COLOR_OPTIONS.map(opt => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setColor(opt.id)}
                    className={`w-8 h-8 rounded-full border-2 transition-all ${color === opt.id ? 'border-white scale-110' : 'border-transparent opacity-50 hover:opacity-80'}`}
                    style={{ backgroundColor: opt.hex }}
                    title={opt.label}
                  />
                ))}
              </div>
            </div>

            {/* Preview strip */}
            <div className="flex items-center gap-3 p-3 rounded-lg bg-white/[0.03] border border-white/5">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-black font-bold text-sm shrink-0"
                style={{ backgroundColor: COLOR_OPTIONS.find(c => c.id === color)?.hex || '#A855F7' }}
              >
                {name.charAt(0).toUpperCase() || '?'}
              </div>
              <div>
                <div className="text-sm text-white font-medium">{name || 'Your persona name'}</div>
                <div className="text-[10px] text-zinc-500">{roleTitle || 'Target role not set'}</div>
              </div>
            </div>

            <button
              type="submit"
              disabled={busy || !name.trim()}
              className="w-full flex items-center justify-center gap-2 py-2.5 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-black font-bold rounded-lg text-sm transition-colors"
            >
              {busy ? 'Saving…' : <><span>Go to Dashboard</span><ArrowRight size={15} /></>}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-zinc-600 mt-4">
          You can create up to 4 personas and change this any time in Settings.
        </p>
      </div>
    </div>
  );
}
